import { useState, useMemo, useCallback } from 'react';
import { X as CloseIcon, Search, Upload, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Contract, SelectedContractFunction } from './types';
import type { PolicyFunction } from '@mls-wallet/shared-types';
import { toSelectedContractFunction } from './contract-function';

interface FunctionSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: Contract | null;
  selectedFunctionSelector?: string | null;
  onFunctionSelect: (selectedFunction: SelectedContractFunction) => void;
  allowedFunctions?: PolicyFunction[];
}

/**
 * Function row component - displays function name and argument count
 */
function FunctionRow({
  func,
  isSelected,
  onClick,
  isAllowed = true,
}: {
  func: SelectedContractFunction;
  isSelected: boolean;
  onClick: () => void;
  isAllowed?: boolean;
}): React.ReactElement {
  return (
    <button
      onClick={isAllowed ? onClick : undefined}
      disabled={!isAllowed}
      className={`w-full flex items-center px-[26px] py-[16px] min-h-[72px] border-b border-solid border-zinc-100 last:border-b-0 transition-colors ${
        !isAllowed
          ? 'bg-zinc-50 dark:bg-zinc-900 cursor-not-allowed opacity-60'
          : isSelected
            ? 'bg-slate-50 dark:bg-slate-900 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900'
            : 'bg-white dark:bg-zinc-950 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900'
      }`}
    >
      <div className="flex-1 flex flex-col gap-[5px] items-start min-w-0">
        {/* Function name */}
        <div className="w-full flex items-center justify-between font-medium">
          <p
            className={`text-[18px] font-semibold break-words pr-4 text-left ${!isAllowed ? 'text-zinc-400 dark:text-zinc-500' : 'text-black dark:text-white'}`}
          >
            {func.name}
          </p>
          {!isAllowed && (
            <span className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded">
              Not allowed by policy
            </span>
          )}
        </div>

        {/* Arguments count */}
        <p
          className={`text-[14px] ${!isAllowed ? 'text-zinc-400 dark:text-zinc-500' : 'text-zinc-600 dark:text-zinc-400'}`}
        >
          {func.inputs.length} argument{func.inputs.length !== 1 ? 's' : ''}
        </p>
      </div>
    </button>
  );
}

/**
 * Loading component shown while fetching ABI
 */
function LoadingAbi(): React.ReactElement {
  return (
    <div className="border border-solid border-zinc-200 bg-zinc-50 rounded-[10px] p-[20px]">
      <div className="flex flex-col items-center justify-center gap-[16px] py-[20px]">
        <Loader2 className="w-[32px] h-[32px] text-zinc-600 animate-spin" />
        <div className="flex flex-col gap-[4px] text-center">
          <p className="font-semibold text-[16px] text-zinc-900">
            Loading contract ABI
          </p>
          <p className="text-[14px] text-zinc-600">
            Fetching function signatures from block explorer...
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Upload ABI component shown when no ABI is available
 */
function UploadAbiPrompt({
  contractName,
  onUploadClick,
}: {
  contractName: string;
  onUploadClick: () => void;
}): React.ReactElement {
  return (
    <div className="border border-solid border-zinc-200 bg-zinc-50 rounded-[10px] p-[20px]">
      <div className="flex flex-col gap-[16px]">
        <div className="flex flex-col gap-[8px]">
          <p className="font-semibold text-[16px] text-zinc-900">
            Upload an ABI to select a function
          </p>
          <p className="text-[14px] text-zinc-600">
            No ABI was found for contract{' '}
            <span className="font-semibold">{contractName}</span>. The contract
            may not be verified on the block explorer.
          </p>
        </div>
        <button
          onClick={onUploadClick}
          className="w-full flex items-center justify-center gap-[8px] px-[16px] py-[10px] bg-zinc-900 text-white rounded-[8px] font-medium text-[14px] hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors cursor-pointer"
        >
          <Upload className="w-[16px] h-[16px]" />
          Upload ABI
        </button>
      </div>
    </div>
  );
}

export function FunctionSelectorDialog({
  open,
  onOpenChange,
  contract,
  selectedFunctionSelector,
  onFunctionSelect,
  allowedFunctions = [],
}: FunctionSelectorDialogProps): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Check if a function is allowed by the policy
   * Empty allowedFunctions means any function is allowed.
   * Selectors distinguish overloaded functions that share the same name.
   */
  const isFunctionAllowed = useCallback(
    (functionSelector: string): boolean => {
      if (allowedFunctions.length === 0) return true;
      return allowedFunctions.some(
        (func) => func.selector.toLowerCase() === functionSelector.toLowerCase()
      );
    },
    [allowedFunctions]
  );

  /**
   * Determine if we're still loading the ABI.
   * Loading state is true when:
   * - Contract is selected
   * - Both arrays are empty (not yet fetched from backend)
   * - Contract name is also empty (indicating ABI hasn't been fetched yet)
   */
  const isLoadingAbi =
    contract !== null &&
    contract.contractName === '' &&
    contract.implementationFunctions.length === 0 &&
    contract.proxyFunctions.length === 0;

  // Extract functions from the contract's ABI data
  const functions = useMemo(() => {
    if (!contract) {
      return [];
    }

    // Combine implementation functions and proxy functions
    return [
      ...contract.implementationFunctions.map(toSelectedContractFunction),
      ...contract.proxyFunctions.map(toSelectedContractFunction),
    ];
  }, [contract]);

  // Filter functions based on search query, then sort so allowed functions come first
  const filteredFunctions = useMemo(() => {
    let filteredList = functions;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredList = filteredList.filter((func) =>
        func.name.toLowerCase().includes(query)
      );
    }

    // Sort: allowed functions come first
    filteredList = [...filteredList].sort((a, b) => {
      const aAllowed = isFunctionAllowed(a.selector) ? 1 : 0;
      const bAllowed = isFunctionAllowed(b.selector) ? 1 : 0;
      return bAllowed - aAllowed;
    });

    return filteredList;
  }, [functions, searchQuery, isFunctionAllowed]);

  const handleFunctionSelect = (func: SelectedContractFunction) => {
    onFunctionSelect(func);
    onOpenChange(false);
  };

  const handleUploadClick = () => {
    window.alert('TODO: implement ABI upload');
  };

  /**
   * Check if contract has ABI data (either implementation or proxy functions).
   */
  const hasAbi =
    contract !== null &&
    (contract.implementationFunctions.length > 0 ||
      contract.proxyFunctions.length > 0);

  /**
   * Determine what content to show:
   * 1. Show loading if ABI is still being fetched
   * 2. Show upload prompt if ABI fetch is done but no functions found
   * 3. Show function list if ABI loaded successfully
   */
  const shouldShowLoading = isLoadingAbi;
  const shouldShowUploadPrompt = !isLoadingAbi && !hasAbi;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-auto min-w-[609px] max-w-[min(980px,calc(100vw-2rem))] min-h-[500px] p-0 gap-0 [&>button]:hidden flex flex-col"
        style={{ minHeight: 500, display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="flex flex-col gap-[22px] p-[20px] relative flex-shrink-0">
          <div className="flex items-center justify-between px-[12px]">
            <DialogTitle className="font-semibold text-[16px] text-zinc-600">
              Select a Function
            </DialogTitle>
            <DialogDescription className="sr-only">
              Choose a contract function allowed by the selected policy.
            </DialogDescription>
          </div>

          {/* Search Bar - always shown */}
          <div className="bg-white border border-solid border-zinc-200 rounded-[41px] h-[39px] relative">
            <div className="flex items-center gap-[10px] px-[22px] py-[10px] h-[39px]">
              <Search className="w-[16px] h-[16px] text-zinc-500 shrink-0" />
              <input
                type="text"
                placeholder="Search functions"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none border-none font-normal text-[16px] text-zinc-900 placeholder:text-zinc-500"
              />
            </div>
          </div>

          {/* Close Button */}
          <button
            className="absolute right-[20px] top-[8px] p-[15px] rounded-[58px] hover:bg-zinc-100 bg-transparent dark:hover:bg-zinc-800 transition-colors"
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className="w-[18px] h-[18px]" />
          </button>
        </div>

        {/* Function List, Loading, or Upload Prompt */}
        <div className="px-[20px] pb-[20px] flex-1 flex flex-col">
          {shouldShowLoading ? (
            <LoadingAbi />
          ) : shouldShowUploadPrompt ? (
            <UploadAbiPrompt
              contractName={contract?.contractName || 'Unknown'}
              onUploadClick={handleUploadClick}
            />
          ) : (
            <div
              className={`border border-solid border-zinc-200 rounded-[10px] max-h-[405px] overflow-y-auto flex-1`}
              style={{ minHeight: 0 }}
            >
              {filteredFunctions.length === 0 ? (
                <div className="flex items-center justify-center h-[48px] text-zinc-500">
                  {functions.length === 0
                    ? 'No functions available'
                    : 'No functions found'}
                </div>
              ) : (
                filteredFunctions.map((func) => (
                  <FunctionRow
                    key={func.selector}
                    func={func}
                    isSelected={
                      selectedFunctionSelector?.toLowerCase() ===
                      func.selector.toLowerCase()
                    }
                    onClick={() => handleFunctionSelect(func)}
                    isAllowed={isFunctionAllowed(func.selector)}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
